import { useEffect, useRef, useState } from 'react';
import { useRoleSkillIds, useSkillsAndRoles } from '@/hooks/useSkillsAndRoles';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import { validateProfileText } from '@/lib/auth-validation';
import backIcon from '../assets/back.svg';
import { Button } from '@/components/ui/Button';
import type { Proficiency } from '@/types/session';

export interface QualificationsValues {
  bio: string;
  background: string;
  proficiency: Proficiency;
  roleIds: number[];
  skillIds: number[];
}
interface Props {
  initialBio?: string;
  initialBackground?: string;
  initialProficiency?: Proficiency | null;
  initialRoleIds?: number[];
  initialSkillIds?: number[];
  onBack: (values: QualificationsValues) => void;
  onSubmit: (values: QualificationsValues) => void;
  isSubmitting: boolean;
  errorMessage?: string;
}
const fieldClass =
  'w-full min-h-[44px] px-4 py-2 bg-[--base-surface-1] border border-[--base-border] rounded-[--radi-6] text-[--text-dark-primary] [color-scheme:dark] [&>option]:bg-[--base-surface-1] [&>option]:text-[--text-dark-primary]';
export default function AboutYourselfForm(props: Props) {
  const { backgroundHelperText, skillsHelperText } = useDaoConfig();
  const { selectableRoles, skills, isLoading, isError, rolesQuery } =
    useSkillsAndRoles();
  const [bio, setBio] = useState(props.initialBio ?? '');
  const [background, setBackground] = useState(props.initialBackground ?? '');
  const [proficiency, setProficiency] = useState<Proficiency>(
    props.initialProficiency ?? 'junior'
  );
  const [roleIds, setRoleIds] = useState(props.initialRoleIds ?? []);
  const [skillIds, setSkillIds] = useState(props.initialSkillIds ?? []);
  const roleSkills = useRoleSkillIds(roleIds);
  const allowedSkillIds = new Set(
    roleIds.flatMap((id) => roleSkills.data?.[id] ?? [])
  );
  const [error, setError] = useState('');
  const [roleSearch, setRoleSearch] = useState('');
  const [skillSearch, setSkillSearch] = useState('');
  const [rolesOpen, setRolesOpen] = useState(false);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const roleSearchRef = useRef<HTMLInputElement>(null);
  const roleTriggerRef = useRef<HTMLButtonElement>(null);
  const catalogDisabled = props.isSubmitting || isLoading || isError;
  const skillsDisabled =
    catalogDisabled ||
    !roleIds.length ||
    roleSkills.isPending ||
    roleSkills.isError;
  const matchingRoles = selectableRoles.filter(
    (role) =>
      !roleIds.includes(role.id) &&
      role.name.toLowerCase().includes(roleSearch.trim().toLowerCase())
  );
  const matchingSkills = skills.filter(
    (skill) =>
      allowedSkillIds.has(skill.id) &&
      !skillIds.includes(skill.id) &&
      skill.name.toLowerCase().includes(skillSearch.trim().toLowerCase())
  );
  useEffect(() => {
    if (rolesOpen) roleSearchRef.current?.focus();
  }, [rolesOpen]);
  const addRole = (id: number) => {
    setRoleIds([...roleIds, id]);
    setRoleSearch('');
    setRolesOpen(false);
    roleTriggerRef.current?.focus();
  };
  const addSkill = (id: number) => {
    if (!allowedSkillIds.has(id)) return;
    setSkillIds([...skillIds, id]);
    setSkillSearch('');
    setSkillsOpen(false);
  };
  return (
    <form
      aria-label="About yourself"
      className="[&_#roleChoices_button]:!w-full [&_#skillChoices_button]:!w-full [&_button[aria-controls]]:!w-full"
      onSubmit={(event) => {
        event.preventDefault();
        setError('');
        try {
          validateProfileText(bio, 'Biography', 4096, true);
          validateProfileText(background, 'Background', 4096, true);
          if (!roleIds.length || !skillIds.length)
            throw new Error('Select at least one role and skill.');
          if (
            roleIds.some(
              (id) => !selectableRoles.some((role) => role.id === id)
            ) ||
            skillIds.some((id) => !skills.some((skill) => skill.id === id))
          ) {
            throw new Error(
              'A selected qualification is no longer in the catalog. Remove it and choose an available entry.'
            );
          }
          if (skillIds.some((id) => !allowedSkillIds.has(id)))
            throw new Error(
              'Select skills associated with your selected roles.'
            );
          props.onSubmit({ bio, background, proficiency, roleIds, skillIds });
        } catch (reason) {
          setError(
            reason instanceof Error
              ? reason.message
              : 'Check your qualifications.'
          );
        }
      }}
    >
      <div className="title">
        <h1 className="text-[30px] font-bold leading-[42px]">
          Tell us about yourself
        </h1>
        <p>Help us get to know you better.</p>
      </div>
      <div>
        <label className="mb-2 block" htmlFor="bioText">
          Bio
        </label>
        <textarea
          id="bioText"
          className={`${fieldClass} h-[56px] resize-y text-sm font-medium`}
          placeholder="Tell us about yourself"
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          required
          disabled={props.isSubmitting}
        />
        <p className="mt-2 text-xs text-[--text-dark-secondary]">
          Short intro (who you are, what you do)
        </p>
      </div>
      <div>
        <label className="mb-2 block" htmlFor="backgroundText">
          Background
        </label>
        <textarea
          id="backgroundText"
          className={`${fieldClass} h-[56px] resize-y text-sm font-medium`}
          placeholder="Your professional background"
          value={background}
          onChange={(e) => setBackground(e.target.value)}
          required
          disabled={props.isSubmitting}
        />
        <p className="mt-2 text-xs text-[--text-dark-secondary]">
          {backgroundHelperText}
        </p>
      </div>
      {isLoading && <p role="status">Loading roles and skills…</p>}
      {isError && (
        <div role="alert">
          Cannot load the catalog.{' '}
          <Button type="button" onClick={() => void rolesQuery.refetch()}>
            Retry catalog
          </Button>
        </div>
      )}
      <div
        className="relative"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget))
            setRolesOpen(false);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setRolesOpen(false);
            roleTriggerRef.current?.focus();
          }
        }}
      >
        <label className="mb-2 block" htmlFor="roleSelect">
          Roles
        </label>
        <button
          id="roleSelect"
          ref={roleTriggerRef}
          type="button"
          className={`${fieldClass} flex h-9 !min-h-9 items-center justify-between !px-3 text-left text-sm`}
          aria-expanded={rolesOpen}
          aria-controls="roleChoices"
          disabled={catalogDisabled}
          onClick={() => setRolesOpen(!rolesOpen)}
        >
          <span>Select your roles</span>
          <i className="ri-arrow-down-s-line text-xl" aria-hidden="true" />
        </button>
        {rolesOpen && !catalogDisabled && (
          <div
            id="roleChoices"
            className="absolute left-0 top-[66px] z-50 !w-[240px] max-w-full !space-y-0 overflow-hidden rounded-[12px] border border-[--base-border] bg-[--base-surface-1] shadow-[--elevation-sm]"
          >
            <div className="flex items-center gap-2 !space-y-0 border-b border-[--base-border] px-3 py-2">
              <i
                className="ri-search-line text-[--text-dark-tertiary]"
                aria-hidden="true"
              />
              <input
                ref={roleSearchRef}
                aria-label="Search roles"
                placeholder="Search"
                value={roleSearch}
                className="!h-7 !border-0 !p-0 text-sm"
                onChange={(event) => setRoleSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    if (matchingRoles[0]) addRole(matchingRoles[0].id);
                  }
                }}
              />
            </div>
            <div className="max-h-[260px] !space-y-0 overflow-y-auto py-1">
              {matchingRoles.map((role) => (
                <button
                  type="button"
                  key={role.id}
                  className="px-3 py-2 text-left text-sm hover:bg-[--base-surface-2] focus-visible:bg-[--base-surface-2]"
                  onClick={() => addRole(role.id)}
                >
                  {role.name}
                </button>
              ))}
              {!matchingRoles.length && (
                <p
                  role="status"
                  className="px-3 py-2 text-sm text-[--text-dark-secondary]"
                >
                  No matching roles
                </p>
              )}
            </div>
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {roleIds.map((id) => (
            <button
              type="button"
              key={id}
              disabled={catalogDisabled || roleSkills.isPending}
              className="!w-auto rounded-[8px] border border-[--base-border] bg-[--base-surface-2] px-3 py-1 text-xs text-[--text-dark-primary]"
              aria-label={`Remove role ${selectableRoles.find((role) => role.id === id)?.name ?? id}`}
              onClick={() => {
                const remaining = roleIds.filter((value) => value !== id);
                const allowed = new Set(
                  remaining.flatMap((role) => roleSkills.data?.[role] ?? [])
                );
                setRoleIds(remaining);
                setSkillIds(skillIds.filter((skill) => allowed.has(skill)));
                setSkillSearch('');
                setSkillsOpen(false);
              }}
            >
              {selectableRoles.find((role) => role.id === id)?.name ??
                `Unavailable role #${id}`}{' '}
              ×
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="mb-2 block" htmlFor="proficiency">
          Overall experience level
        </label>
        <select
          id="proficiency"
          className={fieldClass}
          value={proficiency}
          onChange={(e) => setProficiency(e.target.value as Proficiency)}
          disabled={props.isSubmitting}
        >
          <option value="junior">Junior</option>
          <option value="mid-level">Mid-level</option>
          <option value="senior">Senior</option>
        </select>
        <p className="mt-2 text-xs text-[--text-dark-tertiary]">
          One experience level for your profile. Junior: &lt;2 years ·
          Mid-level: 2–5 years · Senior: &gt;5 years.
        </p>
      </div>
      <div
        className="relative"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget))
            setSkillsOpen(false);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setSkillsOpen(false);
        }}
      >
        <label className="mb-2 block" htmlFor="skillSelect">
          Skills
        </label>
        <div className="flex min-h-9 flex-wrap items-center gap-1 !space-y-0 rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-1] p-1">
          {skillIds.map((id) => (
            <button
              type="button"
              key={id}
              disabled={props.isSubmitting}
              className="!w-auto rounded-[8px] border border-[--base-border] bg-[--base-surface-2] px-3 py-1 text-xs text-[--text-dark-primary]"
              aria-label={`Remove skill ${skills.find((skill) => skill.id === id)?.name ?? id}`}
              onClick={() =>
                setSkillIds(skillIds.filter((value) => value !== id))
              }
            >
              {skills.find((skill) => skill.id === id)?.name ??
                `Unavailable skill #${id}`}{' '}
              ×
            </button>
          ))}
          <input
            id="skillSelect"
            className="!h-7 !min-h-7 !w-auto min-w-24 flex-1 !border-none !px-2 !py-0 text-sm"
            placeholder={roleIds.length ? 'Add skill' : 'Select a role first'}
            value={skillSearch}
            disabled={skillsDisabled}
            aria-expanded={skillsOpen}
            aria-controls="skillChoices"
            onFocus={() => setSkillsOpen(true)}
            onChange={(event) => {
              setSkillSearch(event.target.value);
              setSkillsOpen(true);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                if (skillsOpen && matchingSkills[0])
                  addSkill(matchingSkills[0].id);
              }
            }}
          />
        </div>
        {skillsOpen && !skillsDisabled && (
          <div
            id="skillChoices"
            className="absolute left-0 z-50 mt-2 max-h-[260px] !space-y-0 overflow-y-auto rounded-[12px] border border-[--base-border] bg-[--base-surface-1] py-1 shadow-[--elevation-sm]"
          >
            {matchingSkills.map((skill) => (
              <button
                type="button"
                key={skill.id}
                className="px-3 py-2 text-left text-sm hover:bg-[--base-surface-2] focus-visible:bg-[--base-surface-2]"
                onClick={() => addSkill(skill.id)}
              >
                {skill.name}
              </button>
            ))}
            {!matchingSkills.length && (
              <p
                role="status"
                className="px-3 py-2 text-sm text-[--text-dark-secondary]"
              >
                No matching skills
              </p>
            )}
          </div>
        )}
        <p className="mt-2 text-xs text-[--text-dark-secondary]">
          {skillsHelperText}
        </p>
      </div>
      {(error || props.errorMessage) && (
        <p role="alert">{error || props.errorMessage}</p>
      )}
      <div className="flex justify-between">
        <Button
          type="button"
          variant="secondary"
          disabled={props.isSubmitting}
          onClick={() =>
            props.onBack({ bio, background, proficiency, roleIds, skillIds })
          }
        >
          <img src={backIcon} width={24} height={24} alt="" />
          Back
        </Button>
        <Button type="submit" disabled={skillsDisabled}>
          Continue
        </Button>
      </div>
    </form>
  );
}
